/**
 * DrillDownModal — the container for the whole drill-down chain.
 *
 *   Trial Balance row  ->  Ledger (complete Tally ledger view)  ->  Voucher
 *   Journal / any voucher row  ->  Voucher
 *
 * It holds a navigation STACK rather than a single target, so drilling from a
 * ledger into a voucher keeps the ledger underneath it and the breadcrumb can
 * walk back. Only the top of the stack fetches — the levels below are inert
 * until you return to them.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LedgerStatementView from './LedgerStatementView';
import VoucherDetailView from './VoucherDetailView';
import InventorySummaryView from './InventorySummaryView';
import { useLedgerStatement, useVoucherDetail, useInventorySummary } from '../hooks/useDashboard';
import './DrillDown.css';

/** Fetches only when this level is the active one — see useLedgerStatement. */
const LedgerLevel = ({ level, active, onOpenVoucher }) => {
  const { data, loading, loadingAll, error, loadAll, nextPage, prevPage } = useLedgerStatement(
    active ? level.companyId : null,
    {
      ledgerGuid: level.ledgerGuid,
      ledgerName: level.ledgerName,
      from: level.from,
      to: level.to,
    }
  );
  return (
    <LedgerStatementView
      data={data}
      loading={loading}
      loadingAll={loadingAll}
      error={error}
      onOpenVoucher={onOpenVoucher}
      onLoadAll={loadAll}
      onNextPage={nextPage}
      onPrevPage={prevPage}
    />
  );
};

const VoucherLevel = ({ level, active }) => {
  const { data, loading, error } = useVoucherDetail(active ? level.companyId : null, level.guid);
  return <VoucherDetailView data={data} loading={loading} error={error} />;
};

const InventoryLevel = ({ level, active }) => {
  const { data, loading, error } = useInventorySummary(
    active ? level.companyId : null
  );

  return (
    <InventorySummaryView
      data={data}
      loading={loading}
      error={error}
    />
  );
};

const DrillDownModal = ({ stack = [], onPush, onPopTo, onClose }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  // Escape closes the top level, or the modal when only one level is open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (stack.length > 1) onPopTo(stack.length - 2);
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [stack.length, onPopTo, onClose]);

  // The page behind must not scroll while the modal is open.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  const top = stack[stack.length - 1];

  const handleOpenVoucher = useCallback((row) => {
    if (!row?.voucherGuid) return;
    onPush({
      type: 'voucher',
      companyId: top.companyId,
      guid: row.voucherGuid,
      label: row.voucherNumber || 'Voucher',
    });
  }, [onPush, top]);

const title = useMemo(() => {
  if (!top) return "";

  if (top.type === "ledger")
    return top.ledgerName || "Ledger";

  if (top.type === "inventory")
    return "Opening Stock";

  return top.label || "Voucher";
}, [top]);

const subtitle = useMemo(() => {
  if (!top) return "";

  if (top.type === "ledger") {
    return "Complete ledger view — opening, every posting with its contra entry, running balance, closing.";
  }

  if (top.type === "inventory") {
    return "Inventory items contributing to Opening Stock.";
  }

  return "Complete voucher — every ledger line with its debit/credit split, exactly as Tally records it.";
}, [top]);

  if (!top) return null;

  return (
    <div
      className="dd-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="dd-modal" style={{ opacity: mounted ? 1 : 0, transition: 'opacity 120ms ease' }}>
        <div className="dd-head">
          <div style={{ minWidth: 0 }}>
            {stack.length > 1 && (
              <nav className="dd-crumbs" aria-label="Drill-down path">
                {stack.map((lvl, i) => (
                  <React.Fragment key={`${lvl.type}-${lvl.guid || lvl.ledgerGuid || i}`}>
                    {i > 0 && <span aria-hidden="true">›</span>}
                    <button
                      type="button"
                      className="dd-crumb"
                      disabled={i === stack.length - 1}
                      onClick={() => onPopTo(i)}
                    >
                      {lvl.type === 'ledger' ? (lvl.ledgerName || 'Ledger') : (lvl.label || 'Voucher')}
                    </button>
                  </React.Fragment>
                ))}
              </nav>
            )}
            <h2 className="dd-title">{title}</h2>
            <p className="dd-subtitle">{subtitle}</p>
          </div>

          <div className="dd-actions">
            {stack.length > 1 && (
              <button type="button" className="dd-btn" onClick={() => onPopTo(stack.length - 2)}>
                ← Back
              </button>
            )}
            <button type="button" className="dd-btn dd-btn-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

       <div className="dd-body">

  {top.type === "ledger" && (
    <LedgerLevel
      level={top}
      active
      onOpenVoucher={handleOpenVoucher}
    />
  )}

  {top.type === "voucher" && (
    <VoucherLevel
      level={top}
      active
    />
  )}

  {top.type === "inventory" && (
    <InventoryLevel
      level={top}
      active
    />
  )}

</div>
      </div>
    </div>
  );
};

export default DrillDownModal;
